import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    Snackbar,
    Skeleton,
    Chip,
    Card,
    CardContent,
    Stack,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    Groups as GroupsIcon,
} from '@mui/icons-material';
import { formatDateTime } from '../../../../utils/dateUtils';
import {
    getPendingFieldWorkerRequests,
    createFieldWorkerRequest,
    deleteFieldWorkerRequest,
    approveOrRejectFieldWorkerRequest,
} from '../../services/fieldWorkerRequestService';

const MIN_PHONE_DIGITS = 10;

const EMPTY_FORM = {
    fieldWorkerFirstName: '',
    fieldWorkerLastName: '',
    fieldWorkerPhoneNumber: '',
};

const formatPhoneInput = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const formatPhoneNumber = (phone) => {
    if (!phone) return '—';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length === 11 && cleaned[0] === '1') {
        return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
};

const getWorkerName = (request) =>
    `${request.fieldWorkerFirstName || ''} ${request.fieldWorkerLastName || ''}`.trim();

const getErrorMessage = (error, fallback) => {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data?.message) return data.message;
    return fallback;
};

const tablePaperSx = {
    borderRadius: '12px',
    overflow: 'auto',
    width: '100%',
    maxWidth: '100%',
    border: '1px solid #e5e7eb',
    boxShadow: 'none',
};

const headerCellSx = {
    fontWeight: 600,
    color: '#0f172a',
    fontSize: { xs: '0.8rem', sm: '0.9rem' },
    py: { xs: 1.5, sm: 2 },
    whiteSpace: 'nowrap',
};

const addButtonSx = {
    backgroundColor: '#1652f0',
    '&:hover': { backgroundColor: '#1441cc' },
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: '8px',
    px: { xs: 2, sm: 3 },
    py: 1,
    boxShadow: '0 2px 8px rgba(22, 82, 240, 0.2)',
    fontSize: { xs: '0.875rem', sm: '1rem' },
};

const pendingChipSx = {
    fontWeight: 600,
    fontSize: '0.75rem',
    backgroundColor: '#fef3c7',
    color: '#92400e',
};

const requestDetailLabelSx = {
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontSize: '0.7rem',
};

const requestCardSx = {
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    boxShadow: 'none',
};

const RequestDetailRow = ({ label, value }) => (
    <Box sx={{ textAlign: 'left' }}>
        <Typography variant="caption" color="text.secondary" sx={requestDetailLabelSx}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ color: '#0f172a', mt: 0.25 }}>
            {value}
        </Typography>
    </Box>
);

const RequestActions = ({
    workerName,
    request,
    isActionLoading,
    canWrite,
    canReview,
    onApprove,
    onReject,
    onDelete,
    layout = 'compact',
}) => {
    if (isActionLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: layout === 'compact' ? 'flex-end' : 'center',
                    py: layout === 'mobile' ? 0.5 : 0,
                }}
            >
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (canReview) {
        if (layout === 'mobile') {
            return (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        fullWidth
                        variant="contained"
                        startIcon={<CheckIcon />}
                        onClick={() => onApprove(request)}
                        aria-label={`Approve ${workerName}`}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '8px',
                            backgroundColor: '#16a34a',
                            boxShadow: '0 1px 3px rgba(22, 163, 74, 0.25)',
                            '&:hover': { backgroundColor: '#15803d' },
                        }}
                    >
                        Approve
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        startIcon={<CloseIcon />}
                        onClick={() => onReject(request)}
                        aria-label={`Reject ${workerName}`}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '8px',
                            backgroundColor: '#dc2626',
                            boxShadow: '0 1px 3px rgba(220, 38, 38, 0.25)',
                            '&:hover': { backgroundColor: '#b91c1c' },
                        }}
                    >
                        Reject
                    </Button>
                </Box>
            );
        }

        return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.75 }}>
                <IconButton
                    onClick={() => onApprove(request)}
                    aria-label={`Approve ${workerName}`}
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        backgroundColor: '#16a34a',
                        color: '#fff',
                        boxShadow: '0 1px 3px rgba(22, 163, 74, 0.25)',
                        '&:hover': { backgroundColor: '#15803d' },
                    }}
                >
                    <CheckIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                    onClick={() => onReject(request)}
                    aria-label={`Reject ${workerName}`}
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        backgroundColor: '#dc2626',
                        color: '#fff',
                        boxShadow: '0 1px 3px rgba(220, 38, 38, 0.25)',
                        '&:hover': { backgroundColor: '#b91c1c' },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Box>
        );
    }

    if (canWrite) {
        if (layout === 'mobile') {
            return (
                <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    onClick={() => onDelete(request)}
                    aria-label={`Delete request for ${workerName}`}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        borderRadius: '8px',
                        borderColor: '#e5e7eb',
                        color: '#64748b',
                        '&:hover': {
                            borderColor: '#dc2626',
                            color: '#dc2626',
                            backgroundColor: 'rgba(220, 38, 38, 0.04)',
                        },
                    }}
                >
                    Cancel Request
                </Button>
            );
        }

        return (
            <IconButton
                onClick={() => onDelete(request)}
                aria-label={`Delete request for ${workerName}`}
                sx={{
                    color: '#64748b',
                    '&:hover': { backgroundColor: 'rgba(100, 116, 139, 0.08)' },
                }}
            >
                <DeleteIcon />
            </IconButton>
        );
    }

    return null;
};

const RequestCard = ({
    request,
    canWrite,
    canReview,
    isActionLoading,
    onApprove,
    onReject,
    onDelete,
}) => {
    const workerName = getWorkerName(request);
    const showActions = canWrite || canReview;

    return (
        <Card sx={requestCardSx}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        mb: 2,
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, color: '#0f172a', textAlign: 'left' }}
                    >
                        {workerName}
                    </Typography>
                    <Chip label={request.status || 'PENDING'} size="small" sx={pendingChipSx} />
                </Box>

                <Stack spacing={1.5}>
                    <RequestDetailRow
                        label="Phone"
                        value={formatPhoneNumber(request.fieldWorkerPhoneNumber)}
                    />
                    <RequestDetailRow label="Submitted" value={formatDateTime(request.timeStamp)} />
                    {canReview && (
                        <RequestDetailRow
                            label="Requested By"
                            value={request.requesterName || '—'}
                        />
                    )}
                </Stack>

                {showActions && (
                    <Box
                        sx={{
                            mt: 2.5,
                            pt: 2,
                            borderTop: '1px solid #f1f5f9',
                        }}
                    >
                        <RequestActions
                            workerName={workerName}
                            request={request}
                            isActionLoading={isActionLoading}
                            canWrite={canWrite}
                            canReview={canReview}
                            onApprove={onApprove}
                            onReject={onReject}
                            onDelete={onDelete}
                            layout="mobile"
                        />
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

const RequestCardSkeleton = ({ canReview, showActions }) => (
    <Card sx={{ ...requestCardSx, mb: 2 }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <Skeleton variant="text" width="55%" height={28} />
                <Skeleton variant="rounded" width={72} height={24} />
            </Box>
            <Stack spacing={1.5}>
                <Skeleton variant="text" width="70%" height={36} />
                <Skeleton variant="text" width="80%" height={36} />
                {canReview && <Skeleton variant="text" width="65%" height={36} />}
            </Stack>
            {showActions && (
                <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid #f1f5f9' }}>
                    <Skeleton variant="rounded" width="100%" height={40} />
                </Box>
            )}
        </CardContent>
    </Card>
);

const AddCrewMemberModal = ({ open, onClose, onSubmitted, projectId }) => {
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) {
            setFormData(EMPTY_FORM);
            setFormErrors({});
            setError(null);
        }
    }, [open]);

    const handleFormChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (formErrors[field]) {
            setFormErrors((prev) => ({ ...prev, [field]: null }));
        }
    };

    const handlePhoneChange = (value) => {
        handleFormChange('fieldWorkerPhoneNumber', formatPhoneInput(value));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.fieldWorkerFirstName.trim()) {
            errors.fieldWorkerFirstName = 'First name is required';
        }
        if (!formData.fieldWorkerLastName.trim()) {
            errors.fieldWorkerLastName = 'Last name is required';
        }
        const phoneDigits = formData.fieldWorkerPhoneNumber.replace(/\D/g, '');
        if (!phoneDigits) {
            errors.fieldWorkerPhoneNumber = 'Phone number is required';
        } else if (phoneDigits.length < MIN_PHONE_DIGITS) {
            errors.fieldWorkerPhoneNumber = `Phone number must be at least ${MIN_PHONE_DIGITS} digits`;
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            setError(null);
            await createFieldWorkerRequest({
                projectId,
                fieldWorkerFirstName: formData.fieldWorkerFirstName.trim(),
                fieldWorkerLastName: formData.fieldWorkerLastName.trim(),
                fieldWorkerPhoneNumber: formData.fieldWorkerPhoneNumber.replace(/\D/g, ''),
            });
            onSubmitted();
            onClose();
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to submit request. Please try again.'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle
                component="div"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    pb: 1,
                }}
            >
                <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                    Add Crew Member
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    aria-label="Close"
                    sx={{ flexShrink: 0, ml: 'auto' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, px: 3 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3, mt: 1, textAlign: 'left' }}
                >
                    Submit a request to add a field worker to this project. An internal reviewer
                    will approve or reject the request.
                </Typography>

                <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="First Name"
                            value={formData.fieldWorkerFirstName}
                            onChange={(e) =>
                                handleFormChange('fieldWorkerFirstName', e.target.value)
                            }
                            error={!!formErrors.fieldWorkerFirstName}
                            helperText={formErrors.fieldWorkerFirstName}
                            disabled={submitting}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Last Name"
                            value={formData.fieldWorkerLastName}
                            onChange={(e) =>
                                handleFormChange('fieldWorkerLastName', e.target.value)
                            }
                            error={!!formErrors.fieldWorkerLastName}
                            helperText={formErrors.fieldWorkerLastName}
                            disabled={submitting}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            label="Phone Number"
                            value={formData.fieldWorkerPhoneNumber}
                            onChange={(e) => handlePhoneChange(e.target.value)}
                            error={!!formErrors.fieldWorkerPhoneNumber}
                            helperText={formErrors.fieldWorkerPhoneNumber}
                            disabled={submitting}
                            inputProps={{ inputMode: 'tel' }}
                        />
                    </Grid2>
                </Grid2>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 2, justifyContent: 'flex-end' }}>
                <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting}
                    sx={{ ...addButtonSx, minWidth: 140 }}
                >
                    {submitting ? <CircularProgress size={20} color="inherit" /> : 'Submit Request'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const FieldWorkerRequests = ({ projectId, canWrite = false, canReview = false }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionRequestId, setActionRequestId] = useState(null);
    const [addModalOpen, setAddModalOpen] = useState(false);

    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        type: null,
        request: null,
    });

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const loadRequests = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getPendingFieldWorkerRequests(projectId);
            setRequests(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error loading field worker requests:', err);
            setError('Failed to load field worker requests. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        if (projectId) {
            loadRequests();
        }
    }, [projectId, loadRequests]);

    const handleRequestSubmitted = () => {
        setSnackbar({
            open: true,
            message: 'Crew member request submitted successfully.',
            severity: 'success',
        });
        loadRequests();
    };

    const openConfirmDialog = (type, request) => {
        setConfirmDialog({ open: true, type, request });
    };

    const closeConfirmDialog = () => {
        setConfirmDialog({ open: false, type: null, request: null });
    };

    const handleConfirmAction = async () => {
        const { type, request } = confirmDialog;
        if (!request) return;

        const requestId = request.fieldWorkerRegistrationRequestsId;
        const workerName = getWorkerName(request);

        try {
            setActionRequestId(requestId);
            closeConfirmDialog();

            if (type === 'delete') {
                await deleteFieldWorkerRequest(requestId);
                setSnackbar({
                    open: true,
                    message: `Request for ${workerName} has been deleted.`,
                    severity: 'success',
                });
            } else if (type === 'approve' || type === 'reject') {
                await approveOrRejectFieldWorkerRequest(requestId, type);
                setSnackbar({
                    open: true,
                    message:
                        type === 'approve'
                            ? `${workerName} has been approved and added to the project.`
                            : `Request for ${workerName} has been rejected.`,
                    severity: 'success',
                });
            }

            await loadRequests();
        } catch (err) {
            setSnackbar({
                open: true,
                message: getErrorMessage(err, 'Action failed. Please try again.'),
                severity: 'error',
            });
        } finally {
            setActionRequestId(null);
        }
    };

    const getConfirmDialogContent = () => {
        const { type, request } = confirmDialog;
        if (!request) return { title: '', body: '' };

        const workerName = getWorkerName(request);
        if (type === 'delete') {
            return {
                title: 'Delete Request',
                body: `Are you sure you want to delete the pending request for ${workerName}?`,
            };
        }
        if (type === 'approve') {
            return {
                title: 'Approve Request',
                body: `Approve ${workerName}? This will create a field worker account and add them to the project.`,
            };
        }
        return {
            title: 'Reject Request',
            body: `Are you sure you want to reject the request for ${workerName}?`,
        };
    };

    const dialogContent = getConfirmDialogContent();
    const showActionsColumn = canWrite || canReview;
    const sectionTitle = canReview
        ? `Pending Crew Add Requests (${requests.length})`
        : `Your Crew Requests (${requests.length})`;
    const sectionDescription = canReview
        ? 'Review requests from subcontractors to add field workers to this project.'
        : 'Track pending requests to add crew members. You can cancel requests that are still awaiting approval.';

    const handleApproveRequest = (request) => openConfirmDialog('approve', request);
    const handleRejectRequest = (request) => openConfirmDialog('reject', request);
    const handleDeleteRequest = (request) => openConfirmDialog('delete', request);

    const renderLoadingSkeleton = () => {
        if (isMobile) {
            return (
                <Stack spacing={0}>
                    {[...Array(3)].map((_, index) => (
                        <RequestCardSkeleton
                            key={index}
                            canReview={canReview}
                            showActions={showActionsColumn}
                        />
                    ))}
                </Stack>
            );
        }

        return (
            <TableContainer component={Paper} sx={tablePaperSx}>
                <Table sx={{ width: '100%' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerCellSx}>Worker</TableCell>
                            <TableCell sx={headerCellSx}>Phone</TableCell>
                            <TableCell sx={headerCellSx}>Submitted</TableCell>
                            {canReview && <TableCell sx={headerCellSx}>Requested By</TableCell>}
                            <TableCell sx={headerCellSx}>Status</TableCell>
                            {showActionsColumn && (
                                <TableCell
                                    sx={{
                                        ...headerCellSx,
                                        width: canReview ? 96 : 60,
                                    }}
                                    align="right"
                                >
                                    Actions
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[...Array(3)].map((_, index) => (
                            <TableRow key={index}>
                                <TableCell>
                                    <Skeleton variant="text" width={140} height={20} />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="text" width={100} height={20} />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="text" width={120} height={20} />
                                </TableCell>
                                {canReview && (
                                    <TableCell>
                                        <Skeleton variant="text" width={100} height={20} />
                                    </TableCell>
                                )}
                                <TableCell>
                                    <Skeleton variant="rounded" width={70} height={24} />
                                </TableCell>
                                {showActionsColumn && (
                                    <TableCell align="right">
                                        <Skeleton
                                            variant="rounded"
                                            width={canReview ? 80 : 28}
                                            height={32}
                                            sx={{ ml: 'auto' }}
                                        />
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    const renderRequestsList = () => {
        if (isMobile) {
            return (
                <Stack spacing={2}>
                    {requests.map((request) => {
                        const requestId = request.fieldWorkerRegistrationRequestsId;
                        return (
                            <RequestCard
                                key={requestId}
                                request={request}
                                canWrite={canWrite}
                                canReview={canReview}
                                isActionLoading={actionRequestId === requestId}
                                onApprove={handleApproveRequest}
                                onReject={handleRejectRequest}
                                onDelete={handleDeleteRequest}
                            />
                        );
                    })}
                </Stack>
            );
        }

        return (
            <TableContainer component={Paper} sx={tablePaperSx}>
                <Table sx={{ width: '100%' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerCellSx}>Worker</TableCell>
                            <TableCell sx={headerCellSx}>Phone</TableCell>
                            <TableCell sx={headerCellSx}>Submitted</TableCell>
                            {canReview && <TableCell sx={headerCellSx}>Requested By</TableCell>}
                            <TableCell sx={headerCellSx}>Status</TableCell>
                            {showActionsColumn && (
                                <TableCell
                                    sx={{
                                        ...headerCellSx,
                                        width: canReview ? 96 : 60,
                                    }}
                                    align="right"
                                >
                                    Actions
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {requests.map((request) => {
                            const requestId = request.fieldWorkerRegistrationRequestsId;
                            const isActionLoading = actionRequestId === requestId;
                            const workerName = getWorkerName(request);

                            return (
                                <TableRow
                                    key={requestId}
                                    sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}
                                >
                                    <TableCell sx={{ py: 2 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600, color: '#0f172a' }}
                                        >
                                            {workerName}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 2 }}>
                                        {formatPhoneNumber(request.fieldWorkerPhoneNumber)}
                                    </TableCell>
                                    <TableCell sx={{ py: 2 }}>
                                        {formatDateTime(request.timeStamp)}
                                    </TableCell>
                                    {canReview && (
                                        <TableCell sx={{ py: 2 }}>
                                            {request.requesterName || '—'}
                                        </TableCell>
                                    )}
                                    <TableCell sx={{ py: 2 }}>
                                        <Chip
                                            label={request.status || 'PENDING'}
                                            size="small"
                                            sx={pendingChipSx}
                                        />
                                    </TableCell>
                                    {showActionsColumn && (
                                        <TableCell align="right" sx={{ py: 2 }}>
                                            <RequestActions
                                                workerName={workerName}
                                                request={request}
                                                isActionLoading={isActionLoading}
                                                canWrite={canWrite}
                                                canReview={canReview}
                                                onApprove={handleApproveRequest}
                                                onReject={handleRejectRequest}
                                                onDelete={handleDeleteRequest}
                                                layout="compact"
                                            />
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    if (error && !loading && requests.length === 0) {
        return (
            <Box sx={{ p: { xs: 2, sm: 0 } }}>
                <Alert severity="error" sx={{ borderRadius: '8px' }}>
                    {error}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', overflow: 'hidden' }}>
            <Grid2 container spacing={0} sx={{ width: '100%', margin: 0 }}>
                <Grid2 size={{ xs: 12 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'flex-start' },
                            gap: { xs: 2, sm: 2 },
                            mb: 1,
                            px: { xs: 2, sm: 0 },
                            width: '100%',
                        }}
                    >
                        <Box sx={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 600,
                                    color: '#0f172a',
                                    fontSize: { xs: '1.1rem', sm: '1.5rem' },
                                    textAlign: 'left',
                                }}
                            >
                                {sectionTitle}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5, textAlign: 'left' }}
                            >
                                {sectionDescription}
                            </Typography>
                        </Box>

                        {canWrite && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setAddModalOpen(true)}
                                sx={{
                                    ...addButtonSx,
                                    alignSelf: { xs: 'flex-start', sm: 'center' },
                                    flexShrink: 0,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Add Crew Member
                            </Button>
                        )}
                    </Box>
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <Box sx={{ px: { xs: 2, sm: 0 }, mt: 2 }}>
                        {loading ? (
                            renderLoadingSkeleton()
                        ) : requests.length === 0 ? (
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    py: 6,
                                    px: 2,
                                    borderRadius: '12px',
                                    border: '1px solid #e5e7eb',
                                    backgroundColor: '#fafafa',
                                }}
                            >
                                <GroupsIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 1.5 }} />
                                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                    No pending requests
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {canWrite
                                        ? 'Click Add Crew Member to request someone be added to this project.'
                                        : 'There are no pending crew add requests for this project.'}
                                </Typography>
                            </Box>
                        ) : (
                            renderRequestsList()
                        )}
                    </Box>
                </Grid2>
            </Grid2>

            {canWrite && (
                <AddCrewMemberModal
                    open={addModalOpen}
                    onClose={() => setAddModalOpen(false)}
                    onSubmitted={handleRequestSubmitted}
                    projectId={projectId}
                />
            )}

            <Dialog open={confirmDialog.open} onClose={closeConfirmDialog} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>{dialogContent.title}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        {dialogContent.body}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeConfirmDialog} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmAction}
                        variant="contained"
                        color={confirmDialog.type === 'reject' ? 'error' : 'primary'}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            ...(confirmDialog.type === 'approve' && {
                                backgroundColor: '#16a34a',
                                '&:hover': { backgroundColor: '#15803d' },
                            }),
                        }}
                    >
                        {confirmDialog.type === 'delete'
                            ? 'Delete'
                            : confirmDialog.type === 'approve'
                              ? 'Approve'
                              : 'Reject'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default FieldWorkerRequests;
